import * as d3 from 'd3';
import { selectOrAppend, allValuesFromDateSeriesData } from './index';

export default function() {
  // required paramters
  let width;
  let height;
  let padding;

  // optional parameters
  let isFilled = false;
  let classNameAccessor; // areas have --area suffix, labels have --label suffix
  let frontLineAccessor;
  let strokeAccessor;
  let strokeWidthAccessor;
  let mouseOverAccessor;
  let mouseOutAccessor;
  let events; // [{name: string, nameAnchor: 'start'|'end', from: string (%Y-%m-%d), to: string (%Y-%m-%d or current) (optional)}]
  let hasLabels;
  let labelAccessor; // required if hasLabels is true
  let labelOffsetYAccessor; // optional even if hasLabels is true

  const chart = function(selection) {
    selection.each(function({lines: lineData, dates}) {
      const container = d3.select(this);

      const xScale = d3.scaleTime()
        .domain(d3.extent(dates))
        .range([padding[3], width - padding[1]]);

      const yScale = d3.scaleLinear()
        .domain(d3.extent(allValuesFromDateSeriesData(lineData)))
        .range([height - padding[2], padding[0]]);

      const timeParse = d3.timeParse('%Y-%m-%d');

      // events
      if (events != null) {
        const lineExtends = 32;
        const g = selectOrAppend(container, 'g.events');
        g.selectAll('*').remove();
        events.forEach(event => {
          const x1 = xScale(timeParse(event.startDate));
          if (event.endDate) {
            let x2;
            if (event.endDate === 'current') {
              x2 = width - padding[1]
            } else {
              x2 = xScale(timeParse(event.endDate))
            }
            g.append('rect')
              .attr('class', 'span')
              .attr('x', x1)
              .attr('y', padding[0])
              .attr('width', x2 - x1)
              .attr('height', height - (padding[0] + padding[2]))
          }
          g.append('line')
            .attr('class', 'start')
            .attr('x1', x1)
            .attr('y1', padding[0])
            .attr('x2', x1)
            .attr('y2', height - padding[2] + lineExtends);
          g.append('text')
            .attr('class', 'name')
            .attr('x', event.nameAnchor !== 'end' ? x1 + 5 : x1 - 5)
            .attr('y', height - padding[2] + lineExtends - 3)
            .attr('text-anchor', event.nameAnchor !== 'end' ? 'start' : 'end')
            .text(event.name)
        })
      }

      // areas
      if (isFilled) {
        const areas = selectOrAppend(container, 'g.areas')
          .selectAll('path')
          .data(lineData)
          .join('path')
          .attr('d', d => d3.area((_, i) => xScale(dates[i]), height - padding[2], d => yScale(d))(d.values));

        if (classNameAccessor) {
          areas.attr('class', d => `area ${classNameAccessor(d)}--area`);
        }
      }

      // lines
      const lineGen = d3.line()
        .curve(d3.curveCardinal)
        .x((_, i) => xScale(dates[i]))
        .y(d => yScale(d));
      const lines = selectOrAppend(container, 'g.lines').selectAll('path')
        .data(lineData)
        .join('path')
        .attr('d', d => lineGen(d.values));

      if (classNameAccessor) {
        lines.attr('class', classNameAccessor)
      }

      if (strokeAccessor) {
        lines.attr('stroke', strokeAccessor)
      }

      if (strokeWidthAccessor) {
        lines.attr('stroke-width', strokeWidthAccessor)
      }

      if (mouseOverAccessor) {
        lines.on('mouseover', mouseOverAccessor)
      }

      if (mouseOutAccessor) {
        lines.on('mouseout', mouseOutAccessor)
      }

      if (frontLineAccessor) {
        lines.filter(frontLineAccessor)
          .raise()
      }

      // labels
      if (hasLabels) {
        const labels = selectOrAppend(container, 'g.labels')
          .selectAll('text')
          .data(lineData)
          .join('text')
          .attr('x', padding[3] + ((width - (padding[3] + padding[1])) / 2))
          .attr('y', (d, i) => {
            let y0 = yScale(d.values[Math.floor(d.values.length / 2)]);
            let y1;
            if (i < lineData.length - 1) {
              const values = lineData[i + 1].values;
              y1 = yScale(values[Math.floor(values.length / 2)]);
            } else {
              y1 = height - padding[2];
            }
            let offset = 0;
            if (labelOffsetYAccessor) {
              offset = labelOffsetYAccessor(d);
            }
            return y0 + ((y1 - y0) / 2) + offset;
          })
          .attr('alignment-baseline', 'central')
          .text(labelAccessor);

        if (classNameAccessor) {
          labels.attr('class', d => `label ${classNameAccessor(d)}--label`);
        }
      }

      // x-axis
      container.append('g')
        .attr('class', 'axis x-axis')
        .attr('transform', `translate(0 ${height - padding[2]})`)
        .call(d3.axisBottom(xScale));

      // y-axis
      container.append('g')
        .attr('class', 'axis y-axis')
        .attr('transform', `translate(${padding[3]} 0)`)
        .call(d3.axisLeft(yScale));
    })
  };

  chart.width = function(_) {
    if (!arguments.length) return width;
    width = _;
    return chart
  };

  chart.height = function(_) {
    if (!arguments.length) return height;
    height = _;
    return chart
  };

  chart.padding = function(_) {
    if (!arguments.length) return padding;
    padding = _;
    return chart
  };

  chart.classNameAccessor = function(_) {
    if (!arguments.length) return classNameAccessor;
    classNameAccessor = _;
    return chart
  };

  chart.frontLineAccessor = function(_) {
    if (!arguments.length) return frontLineAccessor;
    frontLineAccessor = _;
    return chart
  };

  chart.strokeAccessor = function(_) {
    if (!arguments.length) return strokeAccessor;
    strokeAccessor = _;
    return chart
  };

  chart.strokeWidthAccessor = function(_) {
    if (!arguments.length) return strokeWidthAccessor;
    strokeWidthAccessor = _;
    return chart
  };

  chart.mouseOverAccessor = function(_) {
    if (!arguments.length) return mouseOverAccessor;
    mouseOverAccessor = _;
    return chart
  };

  chart.mouseOutAccessor = function(_) {
    if (!arguments.length) return mouseOutAccessor;
    mouseOutAccessor = _;
    return chart
  };

  chart.events = function(_) {
    if (!arguments.length) return events;
    events = _;
    return chart
  };

  chart.isFilled = function(_) {
    if (!arguments.length) return isFilled;
    isFilled = _;
    return chart;
  };

  chart.hasLabels = function(_) {
    if (!arguments.length) return hasLabels;
    hasLabels = _;
    return chart;
  };

  chart.labelAccessor = function(_) {
    if (!arguments.length) return labelAccessor;
    labelAccessor = _;
    return chart;
  };

  chart.labelOffsetYAccessor = function(_) {
    if (!arguments.length) return labelOffsetYAccessor;
    labelOffsetYAccessor = _;
    return chart;
  };

  return chart
}
